import { CircularProgress, useMediaQuery } from '@material-ui/core';
import {
  Box,
  createStyles,
  Divider,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Theme,
  Typography,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import React from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { CreditMemo, CurrencyType, PayerCreditMemoConnection } from '../gql-types.generated';
import TableHeader, { HeaderCell } from './TableHeader';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    footer: {
      padding: theme.spacing(1, 6, 1, 0),
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
    },
    memoText: {
      margin: theme.spacing(0, 3, 0, 1),
      fontSize: '2rem',
      fontWeight: 'lighter',
    },
    memoTotal: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
    },
    memoAmount: {
      fontSize: '3.5rem',
      fontWeight: 'lighter',
    },
    tableContainer: {
      height: '600px',
      overflow: 'auto',
      textAlign: 'center',
    },
    cell: {
      fontSize: 16,
      padding: '16px 4px',
      wordWrap: 'break-word',
      wordBreak: 'break-all',
    },
    firstCell: {
      paddingLeft: '35px',
      fontSize: 16,
      wordWrap: 'break-word',
      wordBreak: 'break-all',
    },
    emptyMessage: {
      fontSize: '14px',
      textAlign: 'center',
      fontStyle: 'oblique',
    },
    loadingSpinner: {
      height: 60,
      width: 60,
      display: 'inline-block',
    },
    errorMessage: {
      fontSize: '18px',
      textAlign: 'center',
      fontStyle: 'oblique',
      color: 'red',
    },
    ellipsisText: {
      wordWrap: 'break-word',
      lineClamp: 1,
      boxOrient: 'vertical',
      display: 'box',
      overflow: 'hidden',
      wordBreak: 'break-all',
    },
    tableContainerMobile: {
      height: '70vh',
      overflow: 'auto',
      textAlign: 'center',
    },
    footerMobile: {
      display: 'inline-flex',
    },
    memoAmountMobile: {
      marginLeft: 5,
    },
  }),
);
interface CreditMemosOpenListProps {
  openCreditMemoConnection?: PayerCreditMemoConnection;
  openCreditMemos?: CreditMemo[];
  openCreditMemosError?: Error;
  loadPage: () => void;
  currency?: CurrencyType;
}

const CreditMemosOpenList: React.FC<CreditMemosOpenListProps> = props => {
  const { openCreditMemoConnection, openCreditMemos, loadPage, currency, openCreditMemosError } = props;
  const classes = useStyles();
  const matches = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const emptyMessage = 'No Open Credit Memos.';
  const errorMessage = 'Unable to load data. Please try again later.';

  const tableHeaderCells: HeaderCell[] = [
    { id: 'id', label: 'Credit Memo #' },
    { id: 'issuedDate', label: 'Date Issued' },
    { id: 'reason', label: 'Reason' },
    { id: 'issuedAmount', label: 'Issued', align: 'right' },
    { id: 'balance', label: 'Remaining Balance', align: 'right' },
    { id: 'action', label: '' },
  ];

  const skeletonDesktop: JSX.Element[] = [];
  for (let i = 0; i < 9; i += 1) {
    skeletonDesktop.push(
      <TableRow>
        <TableCell className={classes.firstCell}>
          <Skeleton height="1.5em" width="80%" />
        </TableCell>
        <TableCell className={classes.cell}>
          <Skeleton height="1.5em" width="80%" />
        </TableCell>
        <TableCell className={classes.cell}>
          <Skeleton height="1.5em" width="70%" />
        </TableCell>
        <TableCell className={classes.cell}>
          <Skeleton height="1.5em" width="60%" style={{ marginLeft: 'auto' }} />
        </TableCell>
        <TableCell className={classes.cell}>
          <Skeleton height="1.5em" width="60%" style={{ marginLeft: 'auto' }} />
        </TableCell>
        <TableCell />
      </TableRow>,
    );
  }

  return (
    <>
      <TableContainer className={matches ? classes.tableContainerMobile : classes.tableContainer}>
        <InfiniteScroll
          pageStart={0}
          loadMore={loadPage}
          hasMore={openCreditMemoConnection?.pageInfo.hasNextPage as boolean}
          loader={
            <div className={classes.loadingSpinner}>
              <CircularProgress key={0} size={42} />
            </div>
          }
          initialLoad={false}
          useWindow={false}
        >
          <Table stickyHeader>
            <>
              <col width="25%" />
              <col width="15%" />
              <col width="25%" />
              <col width="15%" />
              <col width="15%" />
              <col width="5%" />
            </>
            <TableHeader cells={tableHeaderCells} isPaymentsDue={false} />
            <TableBody>
              {openCreditMemosError && (
                <TableRow>
                  <TableCell variant="footer" size="small" colSpan={tableHeaderCells.length} className={classes.errorMessage}>
                    {errorMessage}
                  </TableCell>
                </TableRow>
              )}
              {!openCreditMemosError && !openCreditMemos && <>{skeletonDesktop}</>}
              {!openCreditMemosError && openCreditMemos?.length === 0 && (
                <TableRow>
                  <TableCell variant="footer" size="small" colSpan={tableHeaderCells.length} className={classes.emptyMessage}>
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              )}

              {!openCreditMemosError &&
                openCreditMemos?.map((creditMemo, index) => {
                  return (
                    <TableRow key={creditMemo?.id || index}>
                      <TableCell className={classes.firstCell}>
                        <Typography className={classes.ellipsisText}>{creditMemo.referenceNumber}</Typography>
                      </TableCell>
                      <TableCell className={classes.cell}>{new Date(creditMemo?.createdAt).toLocaleDateString()}</TableCell>
                      <TableCell className={classes.cell}>
                        <Typography className={classes.ellipsisText}>{creditMemo.reason}</Typography>
                      </TableCell>
                      <TableCell className={classes.cell} align="right">
                        {typeof creditMemo.amount === 'number'
                          ? new Intl.NumberFormat('en', {
                              style: 'currency',
                              currency: creditMemo.currency || 'USD',
                              currencyDisplay: 'symbol',
                            }).format(creditMemo.amount / 100)
                          : null}
                      </TableCell>
                      <TableCell className={classes.cell} align="right">
                        {typeof creditMemo.remainingBalance === 'number'
                          ? new Intl.NumberFormat('en', {
                              style: 'currency',
                              currency: creditMemo.currency || 'USD',
                              currencyDisplay: 'symbol',
                            }).format(creditMemo.remainingBalance / 100)
                          : null}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </InfiniteScroll>
      </TableContainer>
      <Divider />
      {matches ? (
        <Box className={classes.footer}>
          <Typography variant="subtitle2">
            {openCreditMemoConnection?.totalCount || 0} {`Credit Memo${openCreditMemoConnection?.totalCount !== 1 ? 's' : ''}`}
          </Typography>
          <Typography variant="h5" className={classes.memoAmountMobile}>
            {new Intl.NumberFormat('en', {
              style: 'currency',
              currency: currency || 'USD',
              currencyDisplay: 'symbol',
            }).format((openCreditMemoConnection?.totalBalance || 0) / 100)}
          </Typography>
        </Box>
      ) : (
        <Box className={classes.footer}>
          <Typography className={classes.memoTotal}>{openCreditMemoConnection?.totalCount || 0}</Typography>
          <Typography className={classes.memoText}>{`Credit Memo${openCreditMemoConnection?.totalCount !== 1 ? 's' : ''}`}</Typography>
          <Typography className={classes.memoAmount}>
            {new Intl.NumberFormat('en', {
              style: 'currency',
              currency: currency || 'USD',
              currencyDisplay: 'symbol',
            }).format((openCreditMemoConnection?.totalBalance || 0) / 100)}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default CreditMemosOpenList;
